import { useAppConfig } from '@/hooks/useAppConfig';

interface StyleModalProps {
  isOpen: boolean;
  selectedId: string | null;
  onClose: () => void;
  onSelect: (styleId: string) => void;
  isVip: boolean;
  templateId?: string;
}

export default function StyleModal({ isOpen, selectedId, onClose, onSelect, isVip, templateId }: StyleModalProps) {
  const config = useAppConfig();
  const { t } = useTranslation();
  const allStyles = config?.styles ?? [];
  const template = config?.templates.find((t) => t.id === templateId);
  const applicableIds = template?.applicable_style_ids;
  const styles = applicableIds && applicableIds.length > 0
    ? allStyles.filter((s) => applicableIds.includes(s.id))
    : allStyles;

  if (!isOpen) return null;

  return (
    <div data-testid="style-modal" className="fixed inset-0 z-50 flex items-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div className="relative w-full bg-white rounded-t-3xl max-h-[70vh] overflow-hidden animate-slide-up">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="text-lg font-bold text-gray-800">{t('gen_selectStyle')}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <div className="px-5 overflow-y-auto max-h-[calc(70vh-60px)]" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
          <div className="grid grid-cols-3 gap-3">
            {styles.map((style) => {
              const isLocked = style.is_vip && !isVip;
              const isSelected = selectedId === style.id;

              return (
                <button
                  key={style.id}
                  data-testid={isLocked ? 'style-item-vip' : `style-item-${style.id}`}
                  onClick={() => {
                    if (isLocked) return;
                    onSelect(style.id);
                    onClose();
                  }}
                  className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all ${
                    isSelected ? 'ring-2 ring-pink-400' : ''
                  } ${isLocked ? 'opacity-60' : 'active:scale-95'}`}
                  disabled={isLocked}
                >
                  <div className="aspect-square w-full">
                    <img
                      src={style.thumbnail_url}
                      alt={style.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  {isLocked && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                        <i className="ri-vip-crown-fill text-amber-500 text-sm"></i>
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/50 to-transparent">
                    <p className="text-white font-medium text-xs text-center">{t(`sty_${style.id}`, { defaultValue: style.name })}</p>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* VIP 提示 */}
          {!isVip && (
            <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50">
              <i className="ri-vip-crown-fill text-amber-500 text-sm"></i>
              <p className="text-xs text-amber-700">{t('gen_vipUnlock')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}